"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMinDrawDownDecimal = getMinDrawDownDecimal;
exports.resolveUpfrontFeeConstants = resolveUpfrontFeeConstants;
exports.getMaxAllowedTermMonths = getMaxAllowedTermMonths;
exports.getTermRequiredMinLineSizeCents = getTermRequiredMinLineSizeCents;
exports.getSliderUwMetadataConstants = getSliderUwMetadataConstants;
exports.extractSliderOfferConstants = extractSliderOfferConstants;
exports.isBuildSliderConfigFailure = isBuildSliderConfigFailure;
exports.buildSliderConfig = buildSliderConfig;
const isNil_1 = __importDefault(require("lodash/isNil"));
const offer_1 = require("../offer");
const bounds_1 = require("./bounds");
const math_1 = require("./math");
const offerDetails_1 = require("./offerDetails");
const offerFilter_1 = require("./offerFilter");
/**
 * The fraction of the line the upfront fee is charged against (Python `minDrawDown`). Mirrors the
 * fee-type branch in `lineSizeConstraintUtils.getLineSizeRequiredToFundAmount`:
 * origination fees hit the whole line, first-draw fees only the activation draw, none is immaterial.
 */
function getMinDrawDownDecimal(feeType, minLineDrawDownForActivation) {
    switch (feeType) {
        case offer_1.UpfrontFeeType.origination:
            return 1;
        case offer_1.UpfrontFeeType.firstDraw:
            return minLineDrawDownForActivation;
        case offer_1.UpfrontFeeType.none:
            return 0;
        default:
            throw new Error(`unexpected upfront fee type: ${feeType}`);
    }
}
/**
 * Resolve an offer's upfront fee (origination-or-first-draw) into the decimal constants both
 * slider builders consume. `oneTimeFeeDecimal` is the resolved fee; `firstDrawFeeDecimal` is the
 * RAW first-draw fee, deliberately kept separate (see `lineSizeToCashAndPayoffCents`).
 */
function resolveUpfrontFeeConstants(offer, minLineDrawDownForActivation) {
    const upfrontFee = (0, offer_1.resolveUpfrontFee)({ originationFeePercent: offer.originationFeePercent, firstDrawFeePercent: offer.firstDrawFeePercent });
    return {
        oneTimeFeeDecimal: upfrontFee.percent,
        firstDrawFeeDecimal: offer.firstDrawFeePercent ?? 0,
        minDrawDownDecimal: getMinDrawDownDecimal(upfrontFee.type, minLineDrawDownForActivation),
    };
}
function getMaxAllowedTermMonths(lineSize, allowedTermMap) {
    const availableTerms = Object.keys(allowedTermMap)
        .map(Number)
        .filter((threshold) => lineSize >= threshold)
        .flatMap((threshold) => allowedTermMap[threshold]);
    return availableTerms.length > 0 ? Math.max(...availableTerms) : undefined;
}
function getTermRequiredMinLineSizeCents(fixedTermMonths, allowedTermMap) {
    const thresholds = Object.keys(allowedTermMap)
        .map(Number)
        .sort((a, b) => a - b);
    for (const threshold of thresholds) {
        if (allowedTermMap[threshold].includes(fixedTermMonths)) {
            return threshold * 100;
        }
    }
    return Infinity;
}
function getSliderUwMetadataConstants(uwMetadata) {
    if (!uwMetadata.allowedInstallmentTermLengthInMonths) {
        throw new Error('allowedInstallmentTermLengthInMonths is required for slider - every policy defines this on basePolicy');
    }
    return {
        minLineSizeCents: (0, math_1.roundDecimalProduct)(uwMetadata.minLineSize, 100),
        allowedInstallmentTermLengthInMonths: uwMetadata.allowedInstallmentTermLengthInMonths,
        debtSwapLineSizeBufferDecimal: uwMetadata.debtSwapLineSizeBufferDecimal ?? 0,
        minLineDrawDownForActivation: uwMetadata.minLineDrawDownForActivation ?? 1,
    };
}
function extractBandedSliderOfferConstants(offer, sharedConstants, effectiveMinLineSizeCents) {
    const sortedBands = (offer.lineSizePricingBands ?? []).slice().sort((left, right) => left.minLineSize - right.minLineSize);
    const maxEligibleLineSizeCents = Math.round(sortedBands[sortedBands.length - 1].maxLineSize * 100);
    const constants = [];
    for (const band of sortedBands) {
        const minLineSizeCents = Math.max(effectiveMinLineSizeCents, Math.round(band.minLineSize * 100));
        const maxLineSizeCents = Math.round(band.maxLineSize * 100);
        if (maxLineSizeCents < effectiveMinLineSizeCents) {
            continue;
        }
        if (band.pricing.fixedTermApr === null) {
            return { error: `line-size pricing band ${band.minLineSize}-${band.maxLineSize} has nil fixedTermApr` };
        }
        if (minLineSizeCents > maxLineSizeCents) {
            return { error: `line-size pricing band ${band.minLineSize}-${band.maxLineSize} has an empty selectable range` };
        }
        constants.push({
            ...sharedConstants,
            fixedTermApr: band.pricing.fixedTermApr,
            pricing: band.pricing,
            minLineSizeCents,
            maxLineSizeCents,
            recordingTaxFree: band.recordingTaxFree,
        });
    }
    if (constants.length === 0) {
        return { error: 'lineSizePricingBands do not intersect the selectable line-size range' };
    }
    if (constants[0].minLineSizeCents !== effectiveMinLineSizeCents) {
        return { error: `lineSizePricingBands start at ${constants[0].minLineSizeCents}, expected ${effectiveMinLineSizeCents}` };
    }
    if (constants[constants.length - 1].maxLineSizeCents !== maxEligibleLineSizeCents) {
        return { error: `lineSizePricingBands end at ${constants[constants.length - 1].maxLineSizeCents}, expected ${maxEligibleLineSizeCents}` };
    }
    for (let index = 1; index < constants.length; index += 1) {
        const previous = constants[index - 1];
        const current = constants[index];
        if (current.minLineSizeCents !== previous.maxLineSizeCents + 1) {
            return {
                error: `lineSizePricingBands are not contiguous between ${previous.maxLineSizeCents} and ${current.minLineSizeCents}`,
            };
        }
    }
    return constants;
}
function tryExtractSliderOfferConstants(offer, uwMetadataConstants) {
    const lineSizeCents = (0, math_1.roundDecimalProduct)(offer.lineSize ?? 0, 100);
    if (!offer.fixedTermMonths || offer.fixedTermMonths <= 0) {
        return { error: `fixedTermMonths=${offer.fixedTermMonths}` };
    }
    if (offer.fixedTermApr === undefined || offer.fixedTermApr === null) {
        return { error: 'fixedTermApr is nil' };
    }
    if (!offer.identifier) {
        return { error: 'identifier is falsy' };
    }
    if (!offer.uwSpecId) {
        return { error: 'uwSpecId is falsy' };
    }
    const { oneTimeFeeDecimal, firstDrawFeeDecimal, minDrawDownDecimal } = resolveUpfrontFeeConstants(offer, uwMetadataConstants.minLineDrawDownForActivation);
    if ((0, isNil_1.default)(offer.recordingChargesFixedComponentDollars) || (0, isNil_1.default)(offer.recordingChargesVariableComponentDecimal)) {
        return { error: 'recordingCharges components nil' };
    }
    const termMinLineSizeCents = getTermRequiredMinLineSizeCents(offer.fixedTermMonths, uwMetadataConstants.allowedInstallmentTermLengthInMonths);
    if (termMinLineSizeCents === Infinity) {
        return { error: `no min line size threshold allows fixedTermMonths=${offer.fixedTermMonths}` };
    }
    const perOfferMinLineSizeCents = typeof offer.minLineSize === 'number' && Number.isFinite(offer.minLineSize) && offer.minLineSize > 0 ? (0, math_1.roundDecimalProduct)(offer.minLineSize, 100) : null;
    const effectiveMinLineSizeCents = Math.max(perOfferMinLineSizeCents ?? uwMetadataConstants.minLineSizeCents, termMinLineSizeCents);
    if (effectiveMinLineSizeCents > lineSizeCents) {
        return { error: `effectiveMinLineSizeCents=${effectiveMinLineSizeCents} > lineSizeCents=${lineSizeCents}` };
    }
    const sharedConstants = {
        identifier: offer.identifier,
        uwSpecId: offer.uwSpecId,
        fixedTermMonths: offer.fixedTermMonths,
        firstDrawFeeDecimal,
        oneTimeFeeDecimal,
        minDrawDownDecimal,
        recordingTaxFixedCents: (0, math_1.roundDecimalProduct)(offer.recordingChargesFixedComponentDollars, 100),
        recordingTaxVariableDecimal: offer.recordingChargesVariableComponentDecimal,
        recordingTaxFree: false,
        // priorLienPayoffFee is only populated for refi offers (via offer_prior_lien_payoff_terms).
        // balanceTransferFeePercent may be null; both are multiplied by payoff amounts that are 0
        // when the corresponding feature (refi / debt swap) isn't in play, so defaulting to 0 is
        // semantically equivalent.
        balanceTransferFeePercent: offer.balanceTransferFeePercent ?? 0,
        priorLienPayoffFee: offer.priorLienPayoffFee ?? 0,
        refiPayoffCents: (0, offerDetails_1.getOfferRefiPayoffCents)(offer),
        refiPositions: (0, offerDetails_1.getOfferRefiPositions)(offer),
        refiLiensDetails: offer.lienRefiOfferTerms?.refiLiensDetails ?? [],
        refiLenderName: (0, offerDetails_1.getOfferRefiLenderName)(offer),
        secondLienBalanceBufferPercent: offer.refiLineSizeBufferDecimal ?? offer.helocRefiDetails?.secondLienBalanceBufferPercent ?? 0,
    };
    if (offer.lineSizePricingBands && offer.lineSizePricingBands.length > 0) {
        return extractBandedSliderOfferConstants(offer, sharedConstants, effectiveMinLineSizeCents);
    }
    return [
        {
            ...sharedConstants,
            fixedTermApr: offer.fixedTermApr,
            pricing: null,
            minLineSizeCents: effectiveMinLineSizeCents,
            maxLineSizeCents: lineSizeCents,
        },
    ];
}
function extractSliderOfferConstants(offers, uwMetadataConstants, drops) {
    const all = [];
    for (const offer of offers) {
        const result = tryExtractSliderOfferConstants(offer, uwMetadataConstants);
        if ('error' in result) {
            drops?.push({ identifier: offer.identifier, error: result.error });
        }
        else {
            all.push(...result);
        }
    }
    return all;
}
function isBuildSliderConfigFailure(result) {
    return 'error' in result;
}
function buildSliderConfig(underwritingResult, selectedDebtSwapTradelines, uwMetadataConstants, selectedFirstDrawFee, options = {}) {
    const totalOffers = underwritingResult.preQualOffers?.length ?? 0;
    const preQualOffers = (0, offerFilter_1.filterOffersByFirstDrawFee)(underwritingResult.preQualOffers ?? [], selectedFirstDrawFee);
    const drops = [];
    const fail = (error) => ({ error, totalOffers, offersAfterFeeFilter: preQualOffers.length, drops });
    const sliderOfferConstants = extractSliderOfferConstants(preQualOffers, uwMetadataConstants, drops);
    if (sliderOfferConstants.length === 0) {
        return fail('all offers dropped by extractSliderOfferConstants');
    }
    const selectedDebtSwapPayoffCents = selectedDebtSwapTradelines.reduce((sum, tradeline) => sum + tradeline.balanceCents, 0);
    const fundableOfferConstants = sliderOfferConstants.flatMap((offer) => {
        const feasibility = (0, offerDetails_1.getSliderOfferPayoffFeasibility)(offer, selectedDebtSwapPayoffCents, uwMetadataConstants.debtSwapLineSizeBufferDecimal, options.roundRefiAdditionalFloor);
        return feasibility.isFeasible ? [{ ...offer, minLineSizeCents: Math.max(offer.minLineSizeCents, feasibility.requiredGrossLineSizeCents) }] : [];
    });
    if (fundableOfferConstants.length === 0) {
        return fail('no offer can fund committed payoffs');
    }
    const bounds = (0, bounds_1.computeSliderBounds)(fundableOfferConstants, selectedDebtSwapTradelines, options.roundRefiAdditionalFloor);
    if ('error' in bounds) {
        return fail(bounds.error);
    }
    return {
        ...bounds,
        sliderOfferConstants: fundableOfferConstants,
    };
}
